import { useQuery } from "@tanstack/react-query";
import { getJson, PRC_PREFIX } from "./client";

/**
 * Facility commitment vs drawn — `GET /facility-capacity?facility=`.
 *
 * Why this module exists at all: `prc_facility_capacity` (CAP1 $1.7B, CAP2 $1.0B,
 * INTERNAL $2.5B) was populated and live in the warehouse but reached no screen. The
 * block used to be served only inside `/joints/headroom`, and the Joints page that
 * rendered it was deleted — PortfolioRiskConcentration.tsx now redirects /joints to
 * /manage-limits. So the figures were orphaned. The backend endpoint serves the same
 * numbers from the same helpers `/joints/headroom` uses, so the two cannot drift.
 *
 * IMPORTANT — this is not headroom. Per-limit headroom is computed against
 * total_net_investment (external) or total_capital_usd (internal); `totalCommitmentDollars`
 * feeds exactly one subtraction, `remainingFacilityDollars`. Changing a commitment moves
 * nothing on Monitor except the capacity ruler.
 */
export interface FacilityCapacity {
  facilityCode: string;
  totalCommitmentDollars: number;
  drawnDollars: number;
  /** Floored at zero. Check isOverCommitted before reading a zero as "exactly full". */
  remainingFacilityDollars: number;
  /** True when the drawn book runs past the commitment — currently the case for CAP 1. */
  isOverCommitted: boolean;
  overCommittedDollars: number;
  /** INTERNAL measures against a fixed capital base, not a commitment minus a draw. */
  isFixedBase: boolean;
  /** Every row is still flagged pending confirmation from Finance. Caption it. */
  isPlaceholder: boolean;
  sourceNote: string | null;
  /**
   * Present only on the aggregate EXTERNAL block: what the combined figure is made of.
   * Commitments and draws are absolute dollar amounts, so they add up — unlike the
   * per-limit percentages, which are measured against each book's own denominator and are
   * never combined anywhere in the product.
   */
  books?: {
    facilityCode: string;
    totalCommitmentDollars: number;
    drawnDollars: number;
    remainingFacilityDollars: number;
    isOverCommitted: boolean;
  }[];
  /** Books running past their own commitment, even when the total has room. */
  overCommittedBooks?: string[];
}

export interface FacilityCapacityResponse {
  facilityCode: string;
  asOfDate: string;
  capacity: FacilityCapacity;
  /**
   * Sum of every limit's headroom. Deliberately much larger than
   * remainingFacilityDollars — limits overlap rather than partition the facility, so
   * their headrooms double-count the same dollars. The gap is the point.
   */
  sumOfHeadroomsDollars: number;
  limitCount: number;
  notes: string[];
  /** Present once sql/80_ and sql/81_ are applied; absent on an older backend. */
  portfolio?: PortfolioBlock;
}

/**
 * The three ceilings behind the Capacity cards, and the book measured against each.
 *
 * ── `totalPortfolioDollars` is the CAPITAL POOL, not the deployed book ──────────────────
 * It is committed debt ceilings + the equity ceiling — "across N facilities + equity". That
 * is what makes `debtFundedPct` and `equityFundedPct` a mix adding to 100%, rather than two
 * shares of the book that would read 100% / 0% (every contract carries a debt book, so the
 * book is entirely inside the facilities).
 *
 * ── The two ceilings are scoped differently, on purpose ─────────────────────────────────
 *   debtCeilingDollars    portfolio-wide: every live Debt row summed. The facilities fund
 *                         the whole book regardless of whose limits are on screen.
 *   equityCeilingDollars  the SELECTED investor's Internal cap — the ceiling Treasury sets,
 *                         which is what the card's caption says it is. NOT the Equity-type
 *                         row: Wafra funds through debt and has no equity contribution, so
 *                         keying on that would empty the card for the investor holding most
 *                         of the book. For Navitas the two coincide today.
 *
 * ── One book, measured against each ceiling ─────────────────────────────────────────────
 * `totalOutstandingDollars` and its loans/leases split are the same dollars on both ceiling
 * tables. Not double counting — each table asks "how much of THIS ceiling is used", the
 * convention external and internal limits already follow over one population.
 */
export interface PortfolioBlock {
  /** The selected investor. Scopes the equity ceiling and nothing else. */
  investor: string;
  /** Debt facilities behind the pool — the N in "across N facilities + equity". */
  facilityCount: number;
  /** Who provides the debt, for captions. */
  debtInvestors: string[];
  /** Who provides the equity — decides whether the internal card says "Equity ceiling". */
  equityInvestors: string[];
  /**
   * The selected book, GROSS: sum(net_investment_final) over `books`. Not the capital pool
   * — see the note above the interface.
   */
  totalPortfolioDollars: number;
  debtCeilingDollars: number;
  equityCeilingDollars: number;
  /**
   * Shares of the COMMITTED CAPITAL pool (debt ceiling + equity ceiling), so the two still
   * add to 100%. Deliberately not shares of `totalPortfolioDollars`, which is the book:
   * $2.7B of ceilings over a $1.83B book would read 147% debt funded.
   */
  debtFundedPct: number | null;
  equityFundedPct: number | null;
  /** The selected book, NET of IDC: sum(net_investment_final) − sum(idc_balance). */
  totalOutstandingDollars: number;
  /** Which books these figures cover, after Facility, Product and Investor. */
  books?: string[];
  /**
   * CAP 1 / CAP 2, net of IDC. **Absent** — not zero — when the filters exclude that book,
   * which is what makes `CeilingCards` drop the card instead of rendering a $0 that reads
   * as an empty book. A selected book always reports, including a genuine 0.
   */
  loansOutstandingDollars?: number;
  /** CAP 2. Absent when excluded — see `loansOutstandingDollars`. */
  leasesOutstandingDollars?: number;
  /** Signed — negative when the book runs past the committed ceilings. */
  remainingDebtCapacityDollars: number;
  /**
   * APPETITE, not funding: the ceiling we set for the selected investor, and the only
   * per-investor field here. Deliberately outside `totalPortfolioDollars` — adding it
   * counted the same portfolio twice and produced a $5.2B "total portfolio" for Wafra out
   * of a $2.7B facility and a $2.5B cap over the same book.
   */
  internalCapDollars: number | null;
  /** Debt facilities the selected investor funds. Empty for an equity-only investor. */
  investorFacilities: string[];
  /**
   * The selected investor's OWN book: the contracts inside the facilities they fund. Null
   * when they fund none — exposure is not attributed by investor in the warehouse
   * (`bankdwn_daily.investor_code` is a bare numeric with no lookup table, and one code
   * holds 99.7% of the book), so no share can be assigned to equity-only capital.
   */
  investorOutstandingDollars: number | null;
  investorLoansDollars: number | null;
  investorLeasesDollars: number | null;
  investorAttributionNote: string;
  /** Null: no equity-funded slice of the book is recorded. See the interface note. */
  equityOutstandingDollars: number | null;
  /** Null, same reason. */
  remainingEquityCapacityDollars: number | null;
  equityAttributionNote: string;
}

/**
 * What /facility-capacity accepts: a facility code that has risk limits (CAP1, CAP2, ... as the database lists them) or INTERNAL, which
 * are rows in prc_facility_capacity, or EXTERNAL, every such facility summed, computed server-side so the addition happens once.
 */
export type CapacityFacility = string;

export async function fetchFacilityCapacity(
  facility: CapacityFacility,
  asOfDate: string,
  investor = "WAFRA",
  product = "ALL",
): Promise<FacilityCapacityResponse> {
  const params = new URLSearchParams({ facility, as_of: asOfDate, investor });
  // Narrows which books the EXTERNAL aggregate sums, so the cards cannot report capacity
  // the table below them is not showing.
  if (product && product !== "ALL") params.set("product", product);
  return getJson<FacilityCapacityResponse>(`${PRC_PREFIX}facility-capacity?${params.toString()}`);
}

/**
 * `enabled` is exposed for callers that want the hook conditional. Note that "External
 * spans both books so there is nothing to show" is no longer one of those cases — the
 * endpoint now serves facility=EXTERNAL as the sum of the two, so Capacity asks for it
 * rather than rendering nothing on the most common selection.
 */
export function useFacilityCapacity(
  facility: CapacityFacility | null,
  asOfDate: string,
  enabled = true,
  investor = "WAFRA",
  product = "ALL",
) {
  return useQuery({
    queryKey: ["prc-facility-capacity", facility, asOfDate, investor, product],
    queryFn: () =>
      fetchFacilityCapacity(facility as CapacityFacility, asOfDate, investor, product),
    enabled: enabled && facility !== null,
  });
}
